import React, { useState, useEffect } from "react";
import api from "../utils/api";
import apiV2, { orgApi } from "../utils/apiV2";
import { Button } from "../components/ui/button";
import { 
  Zap, 
  Calendar, 
  Users, 
  Package, 
  LogOut, 
  Settings, 
  Activity, 
  HelpCircle, 
  MapPin,
  Menu,
  X,
  AlertTriangle,
  DollarSign,
  Sparkles,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  LayoutDashboard,
  Bell,
  Search,
  User,
  CreditCard,
  Sliders,
  Shield,
  BookOpen,
  FileText,
  Star,
  Download,
  TrendingUp,
  QrCode,
  CheckCircle,
  Copy,
  Mail,
  Phone,
  Link2,
  Check
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
// Clerk disabled - using custom JWT auth
// import { useClerk } from '@clerk/react-router';
import SettingsDialog from "../components/SettingsDialog";
import TeamMembersTab from "../components/TeamMembersTab";
import { navigationGroups, filterNavigationByPermissions } from "../config/navigation";
import { jwtDecode } from 'jwt-decode';
// ZendeskWidget removed - only shows on landing page
import AssetsTab from "../components/AssetsTab";
import SystemHealthTabSimple from "../components/SystemHealthTabSimple";
import OperationsDashboard from "../components/OperationsDashboard";
import CompanyHealthTab from "../components/CompanyHealthTab";
import GlobalSearch from "../components/GlobalSearch";
import HelpPage from "../components/HelpPage";
import CalendarScheduler from "../components/CalendarScheduler";
import NotificationBell from "../components/NotificationBell";
import ApprovalReviewDialog from "../components/ApprovalReviewDialog";
import BrandingTab from "../components/BrandingTab";
import WaiversTabClean from "../components/WaiversTabClean";
import ReviewsTab from "../components/ReviewsTab";
import CheckInTab from "../components/CheckInTab";
import RelationsTab from "../components/RelationsTab";
import NotificationsTab from "../components/NotificationsTab";
import SettingsTabEnhanced from "../components/SettingsTabEnhanced";
import WidgetCustomization from "../components/WidgetCustomizationNew";
import ProfitLossTab from "../components/ProfitLossTab";
import ProfileTab from "../components/ProfileTab";
import PaymentsTabSimple from "../components/PaymentsTabSimple";
import PartnersAndPromosTab from "../components/PartnersAndPromosTab";
import DiscountsTab from "../components/DiscountsTab";
import SharedServicesTab from "../components/SharedServicesTab";
import OnboardingTutorial from "../components/OnboardingTutorial";
import SettingsTab from "../components/SettingsTab";
import RolesTab from "../components/RolesTab";
import ActionsBalance from "../components/ActionsBalance";
import SubscriptionSuccess from "../components/SubscriptionSuccess";
import ServicesTabImproved from "../components/ServicesTabImproved";
import MiniFlowsTab from "../components/MiniFlowsTab";
import RevenueTab from "../components/RevenueTab";
import ExpensesTab from "../components/ExpensesTab";
// PromoCodesTab merged into DiscountsTab (under Customers)
// PayoutsTab + AccountTab merged into PaymentsTabSimple (under Financials)
import SetupGuideWidget from "../components/SetupGuideWidget";
import TimeclockTab from "../components/TimeclockTab";
import MarketplaceTab from "../components/MarketplaceTab";
import BlogTab from "../components/BlogTab";
import ConversionsTab from "../components/ConversionsTab";
import ScheduleTab from "../components/ScheduleTab";
import PartnersTab from "../components/PartnersTab";
import { BACKEND_URL } from "../config/api";


// BACKEND_URL imported from config/api
const API = `${BACKEND_URL}/api`;

const DashboardV2 = () => {
  const navigate = useNavigate();
  // Clerk disabled - using custom JWT auth
  // const clerk = useClerk();
  
  // Handle payment success redirect from Stripe
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [editBookingId, setEditBookingId] = useState(null);
  const [partnerConnectLoading, setPartnerConnectLoading] = useState(false);
  
  // Handle tab query parameter for deep linking (e.g., from Stripe Connect return)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const connectSuccess = searchParams.get('connect_success');
    const connectedPartnerId = searchParams.get('connected');
    const refreshPartners = searchParams.get('refresh');
    const editBookingParam = searchParams.get('editBookingId');
    const action = searchParams.get('action');
    
    // Skip if we're handling the connect action (handled by other useEffect)
    if (action === 'connect') {
      // Still set the active view to partners while connecting
      setActiveView('partners');
      return;
    }
    
    if (tabParam) {
      // Valid tab views that can be navigated to
      const validTabs = ['home', 'marketplace', 'schedule', 'roles', 'teamMembers', 'assets', 'services', 
        'flows', 'miniFlows', 'profitLoss', 'discounts', 'account', 'payments', 'payouts', 
        'branding', 'waivers', 'notifications', 'reviews', 'partners',
        'checkIn', 'settings', 'help', 'systemHealth', 'companyHealth', 'relations', 'checkin'];
      
      if (validTabs.includes(tabParam)) {
        setActiveView(tabParam);
        
        // Handle edit booking navigation from Schedule tab
        if (editBookingParam && tabParam === 'schedule') {
          setEditBookingId(editBookingParam);
        }
        
        // Show success message for Stripe Connect return (for affiliates)
        if (connectSuccess === 'true' && tabParam === 'discounts') {
          toast.success('Stripe Connect onboarding completed! The affiliate status will update shortly.');
        }
        
        // Show success message for partner Stripe Connect return
        if (connectedPartnerId && tabParam === 'partners') {
          toast.success('Stripe account connected successfully! Your partner status will update shortly.');
        }
        
        // Clear URL params after processing
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams]);
  
  useEffect(() => {
    const paymentSuccess = searchParams.get('payment_success');
    const bookingId = searchParams.get('booking_id');
    const confirmationCode = searchParams.get('confirmation_code');
    
    if (paymentSuccess === 'true' && bookingId) {
      // Fetch booking details and show confirmation modal
      const fetchBookingDetails = async () => {
        try {
          // Try authenticated endpoint first
          const response = await api.get(`/bookings/${bookingId}`);
          if (response.data) {
            setConfirmedBooking({
              ...response.data,
              confirmationCode: confirmationCode || response.data.confirmationCode
            });
            setShowPaymentSuccessModal(true);
            return;
          }
        } catch (err) {
          // Primary fetch failed, trying fallback
        }
        
        // Fallback: Try public trips endpoint with confirmation code
        if (confirmationCode) {
          try {
            const publicResponse = await api.get(`/public/trips/${confirmationCode}`);
            if (publicResponse.data) {
              setConfirmedBooking({
                ...publicResponse.data,
                confirmationCode: confirmationCode
              });
              setShowPaymentSuccessModal(true);
              return;
            }
          } catch (publicErr) {
            // Public trips fallback also failed
          }
        }
        
        // Final fallback: Show basic confirmation with just the code
        setConfirmedBooking({
          confirmationCode: confirmationCode || 'N/A',
          bookingId: bookingId
        });
        setShowPaymentSuccessModal(true);
      };
      fetchBookingDetails();
      
      // Clear URL params
      setSearchParams({});
    }
    
    const paymentCancelled = searchParams.get('payment_cancelled');
    if (paymentCancelled === 'true') {
      toast.error('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Get organization context
  const organizationName = localStorage.getItem('organizationName') || 'BookingFlows';
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userPermissions = userData.permissions || [];
  const userType = localStorage.getItem('userType') || 'admin';
  
  // Get organization branding (color, logo)
  const [orgBranding, setOrgBranding] = useState({
    primaryColor: '#007AFF',
    logoUrl: null
  });
  
  // Decode JWT to get tabPermissions and user info
  const [tabPermissions, setTabPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    const storedUserType = localStorage.getItem('userType');
    
    if (token && storedUserType === 'teamMember') {
      try {
        const decoded = jwtDecode(token);
        const permissions = decoded.tabPermissions || {};
        
        // Team members are NOT admins - they should see limited tabs based on permissions
        setTabPermissions(permissions);
        setIsAdmin(false); // Team members are never admins
        setCurrentUserId(decoded.userId || decoded.teamId); // Get team member ID from token
        setOrganizationId(decoded.organizationId); // Get organization ID from token
      } catch (error) {
        // Token decode error - user may need to re-login
      }
    } else if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsAdmin(true); // Admins see everything
        setCurrentUserId(decoded.userId || null);
        setOrganizationId(decoded.organizationId); // Get organization ID from token
      } catch (error) {
        // Token decode error
      }
    }
  }, [navigate]);
  
  // Handle partner Stripe Connect onboarding from email link
  // This useEffect must be after organizationId state is declared
  useEffect(() => {
    const action = searchParams.get('action');
    const partnerId = searchParams.get('partner_id');
    const tabParam = searchParams.get('tab');
    
    if (action === 'connect' && partnerId && tabParam === 'partners' && organizationId) {
      // Auto-trigger Stripe Connect onboarding for the partner
      const initiateStripeConnect = async () => {
        setPartnerConnectLoading(true);
        try {
          const response = await apiV2.post(
            `/organizations/${organizationId}/partners/${partnerId}/create-onboarding-link`
          );
          
          if (response.data.success && response.data.onboarding_url) {
            // Redirect to Stripe onboarding
            toast.success('Redirecting to Stripe to connect your account...');
            window.location.href = response.data.onboarding_url;
          } else {
            toast.error('Failed to generate Stripe onboarding link');
            setSearchParams({ tab: 'partners' });
          }
        } catch (error) {
          console.error('Stripe Connect error:', error);
          toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
          setSearchParams({ tab: 'partners' });
        } finally {
          setPartnerConnectLoading(false);
        }
      };
      
      initiateStripeConnect();
    }
  }, [searchParams, organizationId, setSearchParams]);
  
  // Detect scroll to add shadow
  useEffect(() => {
    const handleScroll = (e) => {
      const scrolled = e.target.scrollTop > 10;
      setIsScrolled(scrolled);
    };
    
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll);
      return () => mainContent.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Fetch organization branding
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        // Use v2 PostgreSQL API for organization details
        const response = await orgApi.getOrganization();
        if (response.data) {
          setOrgBranding({
            primaryColor: response.data.primaryColor || '#007AFF',
            logoUrl: response.data.logoUrl
          });
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
      }
    };
    
    fetchBranding();
  }, []);
  
  // Helper function to check if user has permission
  const hasPermission = (permission) => {
    // Admins have all permissions
    if (userType === 'admin') return true;
    // Check if team member has specific permission
    return userPermissions.includes(permission);
  };
  
  // Check for payment success/canceled in URL
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get('payment');
  const sessionId = urlParams.get('session_id');
  const showSubscriptionSuccess = paymentStatus === 'success' && sessionId;
  
  const [activeView, setActiveView] = useState(() => {
    // Restore last active tab from localStorage
    return localStorage.getItem('lastActiveTab') || 'home';
  });
  
  // Filter state for Relations tab (set when navigating from Dashboard)
  const [relationsFilter, setRelationsFilter] = useState('all');
  
  // Navigation handler that supports passing filter state
  const handleNavigate = (view, options = {}) => {
    if (view === 'relations' && options.filter) {
      setRelationsFilter(options.filter);
    }
    setActiveView(view);
  };
  
  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('lastActiveTab', activeView);
  }, [activeView]);
  // Sidebar state - persists to localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('sidebarOpen');
    if (saved !== null) {
      return saved === 'true';
    }
    // Default: open on desktop, closed on mobile
    return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true;
  });
  
  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', sidebarOpen.toString());
  }, [sidebarOpen]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [trips, setBookings] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [demoLoading, setDemoLoading] = useState(false);
  
  // Collapsed sections - persist to localStorage
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const saved = localStorage.getItem('collapsedSections');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure internal is collapsed by default if not explicitly set
      if (parsed.internal === undefined) {
        parsed.internal = true;
      }
      return parsed;
    }
    return {
      operations: true, // Default: collapsed - only expand when navigating to section
      financials: true,
      employees: true, // Default: collapsed
      customers: true,
      management: true,
      bookings: true,
      internal: true  // Default: collapsed
    };
  });
  
  // Save collapsed sections to localStorage
  useEffect(() => {
    localStorage.setItem('collapsedSections', JSON.stringify(collapsedSections));
  }, [collapsedSections]);
  
  // Auto-expand section containing current activeView
  useEffect(() => {
    const findGroupForView = (viewId) => {
      for (const group of navigationGroups) {
        if (group.items) {
          if (group.items.find(item => item.id === viewId)) {
            return group.id;
          }
        }
      }
      return null;
    };
    
    const groupId = findGroupForView(activeView);
    if (groupId && collapsedSections[groupId]) {
      setCollapsedSections(prev => ({
        ...prev,
        [groupId]: false // Expand the section containing current view
      }));
    }
  }, [activeView]);
  
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Get org-specific storage key
  const getGuideStorageKey = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return `hideSetupGuide_${payload.organizationId || 'default'}`;
      } catch {
        return 'hideSetupGuide';
      }
    }
    return 'hideSetupGuide';
  };
  
  const [showSetupGuide, setShowSetupGuide] = useState(() => {
    // Show by default unless user has hidden it for THIS org
    const key = getGuideStorageKey();
    return localStorage.getItem(key) !== 'true';
  });
  const [showGuideHighlight, setShowGuideHighlight] = useState(() => {
    // Show highlight if guide is currently hidden
    const key = getGuideStorageKey();
    return localStorage.getItem(key) === 'true';
  });
  const [shouldBounce, setShouldBounce] = useState(false);

  // Check if user has seen onboarding - REMOVED auto-show
  // User can manually trigger it with the "Quick Tour" button
  useEffect(() => {
    // Don't auto-show onboarding anymore
    // Users can click the "Quick Tour" button when they want it
  }, []);

  // Handle window resize - removed auto-close behavior
  // Sidebar state now persists via localStorage
  // Users manually control sidebar with toggle button

  useEffect(() => {
    fetchData();
    // Poll every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    // Listen for navigation events from components
    const handleNavigate = (e) => {
      const targetView = e.detail;
      setActiveView(targetView);
      
      // Expand the Operations section if navigating to operations tabs
      const operationsTabs = ['roles', 'teamMembers', 'assets', 'schedule'];
      if (operationsTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, operations: false }));
      }
      
      // Expand Financials section
      const financialTabs = ['payroll', 'payouts', 'financialOverview', 'partners'];
      if (financialTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, financials: false }));
      }
      
      // Expand Customer section as needed
      const customerTabs = ['services', 'branding', 'waivers', 'customers', 'reviews'];
      if (customerTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, bookings: false }));
      }
      
      // Expand Employees section
      const employeeTabs = ['timeclock', 'worksites'];
      if (employeeTabs.includes(targetView)) {
        setCollapsedSections(prev => ({ ...prev, employees: false }));
      }
    };
    window.addEventListener('navigate', handleNavigate);
    
    // Listen for help guide open events from SetupGuideWidget
    const handleOpenHelpGuide = (e) => {
      const section = e.detail?.section || 'welcome';
      setHelpSection(section);
      setHelpOpen(true);
    };
    window.addEventListener('openHelpGuide', handleOpenHelpGuide);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('navigate', handleNavigate);
      window.removeEventListener('openHelpGuide', handleOpenHelpGuide);
    };
  }, []);

  const fetchData = async () => {
    try {
      // Use v2 PostgreSQL API endpoints
      // Use Promise.allSettled to handle permission errors gracefully
      const [teamMembersRes, assetsRes, tripsRes] = await Promise.allSettled([
        orgApi.getTeamMembers(),
        orgApi.getAssets(),
        orgApi.getBookings(),
      ]);
      
      // Extract data from settled promises, handling permission errors gracefully
      const teamData = teamMembersRes.status === 'fulfilled' 
        ? (teamMembersRes.value.data?.teamMembers || teamMembersRes.value.data || [])
        : [];
      
      const assetsData = assetsRes.status === 'fulfilled'
        ? (assetsRes.value.data?.assets || assetsRes.value.data || [])
        : [];
      
      const bookingsData = tripsRes.status === 'fulfilled'
        ? (tripsRes.value.data?.bookings || tripsRes.value.data || []).map(b => ({
            ...b,
            preferredDate: b.bookingDate,
            preferredTime: b.startTime,
            servicePrice: b.totalPrice
          }))
        : [];
      
      setTeamMembers(teamData);
      setAssets(assetsData);
      setBookings(bookingsData);
      
      // Only show error if ALL requests failed (not just permission denied)
      const allFailed = [teamMembersRes, assetsRes, tripsRes].every(r => r.status === 'rejected');
      if (allFailed) {
        toast.error("Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Set empty arrays on error to prevent undefined issues
      setTeamMembers([]);
      setAssets([]);
      setBookings([]);
      toast.error("Failed to load data");
    }
  };

  const handleLogout = async () => {
    try {
      // Clear all auth data including JWT
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("userType");
      localStorage.removeItem("userData");
      localStorage.removeItem("organizationId");
      localStorage.removeItem("organizationSlug");
      localStorage.removeItem("organizationName");
      localStorage.removeItem("clerkAuth");
      
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local storage
      localStorage.clear();
      navigate("/login");
    }
  };

  const handleSearchNavigate = (tab, itemId) => {
    setActiveView(tab);
    toast.success(`Navigated to ${tab}`);
  };

  const handleUnhideGuide = () => {
    const key = getGuideStorageKey();
    localStorage.removeItem(key);
    setShowSetupGuide(true);
    setShowGuideHighlight(false);
    toast.success('Setup guide enabled!');
  };

  // Listen for guide being hidden from any component
  useEffect(() => {
    const handleGuideHidden = () => {
      const key = getGuideStorageKey();
      const isHidden = localStorage.getItem(key) === 'true';
      if (isHidden) {
        setShowSetupGuide(false);
        setShowGuideHighlight(true);
        setShouldBounce(true);
        
        // Stop bouncing after 5 seconds, but keep blue dot
        setTimeout(() => {
          setShouldBounce(false);
        }, 5000);
      } else {
        setShowSetupGuide(true);
        setShowGuideHighlight(false);
        setShouldBounce(false);
      }
    };
    window.addEventListener('guideVisibilityChanged', handleGuideHidden);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideHidden);
  }, []);

  const handleHelpClick = (section) => {
    setHelpSection(section);
    setHelpOpen(true);
  };

  const handleViewRequests = (requests) => {
    setPendingRequests(requests);
    setApprovalDialogOpen(true);
  };

  const handleApprovalComplete = () => {
    fetchData(); // Refresh team_member data after approval
  };
  
  const handleSimulateDemo = async () => {
    try {
      setDemoLoading(true);
      const response = await api.post(`/simulate-light-show`);
      toast.success(
        `Demo data created! ${response.data.summary.assets} assets, ${response.data.summary.team_members} team_members, ${response.data.summary.trips} trips.`,
        { duration: 6000 }
      );
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error simulating demo:', error);
      toast.error('Failed to create demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  // Filter navigation based on user type and permissions
  const navGroups = filterNavigationByPermissions(tabPermissions, isAdmin);
  
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  return (
    <>
      {/* Partner Stripe Connect Loading Overlay */}
      {partnerConnectLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl p-8 text-center max-w-md mx-4">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connecting to Stripe</h2>
            <p className="text-gray-600">Please wait while we redirect you to Stripe to complete your account setup...</p>
          </div>
        </div>
      )}
      
      {/* Payment Success Modal - Shows when returning from Stripe checkout */}
      {showPaymentSuccessModal && confirmedBooking && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => setShowPaymentSuccessModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header - Green like "Already Paid" */}
            <div className="p-6 text-center bg-gradient-to-r from-green-500 to-green-600">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
              <p className="text-white text-opacity-90">Payment received - Customer notified</p>
            </div>

            {/* Booking Details */}
            <div className="p-6 space-y-4">
              {/* Confirmation Code */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Confirmation Code</p>
                <p className="text-3xl font-mono font-bold text-blue-600">{confirmedBooking.confirmationCode}</p>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="text-sm font-semibold text-gray-900">{confirmedBooking.customerName || 'N/A'}</p>
                  {confirmedBooking.customerEmail && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" />
                      {confirmedBooking.customerEmail}
                    </p>
                  )}
                  {confirmedBooking.customerPhone && (
                    <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />
                      {confirmedBooking.customerPhone}
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {confirmedBooking.preferredDate 
                      ? new Date(confirmedBooking.preferredDate + 'T00:00:00').toLocaleDateString('en-US', { 
                          month: 'short', day: 'numeric', year: 'numeric' 
                        })
                      : 'N/A'}
                  </p>
                  {confirmedBooking.preferredTime && (
                    <p className="text-sm text-gray-600">{confirmedBooking.preferredTime}</p>
                  )}
                </div>

                {confirmedBooking.serviceName && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</p>
                    <p className="text-sm font-semibold text-gray-900">{confirmedBooking.serviceName}</p>
                  </div>
                )}

                {confirmedBooking.groupSize && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Capacity</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {confirmedBooking.groupSize} {confirmedBooking.groupSize === 1 ? 'person' : 'people'}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment</p>
                  <p className="text-sm font-semibold text-green-600">Paid</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-4">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      // Generate magic link for secure ticket access
                      const response = await api.post(`/public/bookings/${confirmedBooking.confirmationCode}/generate-magic-link`);
                      const magicToken = response.data?.magicToken;
                      const link = magicToken 
                        ? `${window.location.origin}/customer-ticket/${confirmedBooking.confirmationCode}?token=${magicToken}`
                        : `${window.location.origin}/customer-ticket/${confirmedBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link with magic access copied!');
                    } catch (err) {
                      // Fallback to basic link if magic link fails
                      const link = `${window.location.origin}/customer-ticket/${confirmedBooking.confirmationCode}`;
                      navigator.clipboard.writeText(link);
                      toast.success('Ticket link copied!');
                    }
                  }}
                  className="w-full"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Ticket Link
                </Button>

                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      // Generate magic link for secure ticket access
                      const response = await api.post(`/public/bookings/${confirmedBooking.confirmationCode}/generate-magic-link`);
                      const magicToken = response.data?.magicToken;
                      const url = magicToken 
                        ? `/customer-ticket/${confirmedBooking.confirmationCode}?token=${magicToken}`
                        : `/customer-ticket/${confirmedBooking.confirmationCode}`;
                      window.open(url, '_blank');
                    } catch (err) {
                      // Fallback if magic link fails
                      window.open(`/customer-ticket/${confirmedBooking.confirmationCode}`, '_blank');
                    }
                  }}
                  className="w-full"
                >
                  View Ticket
                </Button>

                <Button
                  onClick={() => setShowPaymentSuccessModal(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Show subscription success screen if payment successful */}
      {showSubscriptionSuccess && <SubscriptionSuccess />}
      
      {!showSubscriptionSuccess && (
    <div className="flex h-screen font-['Poppins',sans-serif] justify-center bg-gradient-to-br from-gray-50 to-white">
      <div className="flex w-full max-w-[1400px] px-0 lg:px-12 pt-0 lg:pt-8 gap-0 lg:gap-8">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Collapsible with width */}
      <aside
        className={`${
          sidebarOpen ? 'w-56' : 'w-0 lg:w-56'
        } bg-white lg:bg-transparent fixed lg:relative transition-all duration-300 flex flex-col h-full z-50 overflow-hidden max-w-[14rem]`}
      >
        {/* Sidebar Header */}
        <div className="p-4 pt-8 md:pt-12 pb-8">
          {/* Mobile Close Button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
            />
            <h1 className="text-lg font-semibold text-gray-900 leading-tight">
              {organizationName}
            </h1>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-3 pb-4 space-y-2 overflow-y-auto scrollbar-hide">
          {navGroups.map((group) => {
            const GroupIcon = group.icon;
            const isCollapsed = collapsedSections[group.id];
            
            // Handle standalone tabs (like Home)
            if (group.isStandalone) {
              const isActive = activeView === group.id;
              return (
                <button
                  key={group.id}
                  onClick={() => setActiveView(group.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-all ${
                    isActive
                      ? 'bg-gray-200 text-gray-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-base">{group.label}</span>
                </button>
              );
            }
            
            // Collapsible groups
            return (
              <div key={group.id} className="space-y-1.5">
                {/* Group header - clickable to expand/collapse */}
                <button
                  onClick={() => toggleSection(group.id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-all"
                >
                  <span>{group.label}</span>
                  {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {/* Group Items - INDENTED, shown when not collapsed */}
                {!isCollapsed && group.items && group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveView(item.id)}
                      className={`w-full flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-md transition-all ${
                        isActive
                          ? 'bg-gray-200 text-gray-900 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-base">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="ml-auto px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        
        {/* Settings & Help at bottom of sidebar - FIXED POSITION */}
        <div className="px-4 pb-4 space-y-1 border-t pt-4 flex-shrink-0">
          {/* Settings - Only show for admins */}
          {isAdmin && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-base text-gray-700 hover:bg-gray-100 transition-all"
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </button>
          )}
          {/* My Profile - Only show for team members */}
          {!isAdmin && (
            <button
              onClick={() => setSettingsOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-base text-gray-700 hover:bg-gray-100 transition-all"
            >
              <User className="w-5 h-5" />
              <span>My Profile</span>
            </button>
          )}
          <button
            onClick={() => setHelpOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-base text-gray-700 hover:bg-gray-100 transition-all"
          >
            <HelpCircle className="w-5 h-5" />
            <span>Help</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-base text-red-600 hover:bg-red-50 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden lg:pt-0 pt-[72px]">
        {/* Mobile Header - Fixed at top of viewport - 72px height, vertically centered */}
        <div className="lg:hidden fixed top-0 left-0 right-0 flex items-center justify-between px-4 bg-white h-[72px] z-30 shadow-sm">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? (
              <X className="w-7 h-7 text-gray-700" />
            ) : (
              <Menu className="w-7 h-7 text-gray-700" />
            )}
          </button>
          
          {/* Action Icons on Mobile - Top Right - Vertically Centered */}
          {userType === 'admin' && (
            <div className="flex items-center gap-3">
              <NotificationBell onViewRequests={handleViewRequests} />
            </div>
          )}
        </div>

        {/* Content Area - with scroll-padding for sticky header */}
        <div id="main-content" className="flex-1 overflow-y-auto scrollbar-hide scroll-pt-4">
          {/* Icon Bundle - Desktop Only (≥1024px) - Fixed at top right */}
          {userType === 'admin' && (
            <div className="hidden lg:flex fixed top-4 right-6 z-50">
              <div className="flex items-center gap-3 bg-white/95 backdrop-blur-sm shadow-lg rounded-full px-4 py-2">
                <NotificationBell onViewRequests={handleViewRequests} />
              </div>
            </div>
          )}
          
          <div className="px-2 sm:px-4 md:px-4 lg:px-6 pb-32 w-full md:max-w-5xl md:mx-auto pt-2 lg:pt-0">
            
            {/* Content */}
            <div className="lg:mt-12 mt-4 px-1 sm:px-2 md:px-0">
            {activeView === 'schedule' && (
              <CalendarScheduler 
                onRefresh={fetchData} 
                onDemoClick={handleSimulateDemo}
                demoLoading={demoLoading}
                showMyBookingsToggle={!isAdmin}
                teamMemberId={currentUserId}
                editBookingId={editBookingId}
                onEditBookingClear={() => setEditBookingId(null)}
              />
            )}
            {activeView === 'home' && <OperationsDashboard onNavigate={handleNavigate} />}
            {activeView === 'marketplace' && <MarketplaceTab organizationId={organizationId} />}
            {activeView === 'blog' && <BlogTab />}
            {activeView === 'conversions' && <ConversionsTab />}
            {activeView === 'roles' && <RolesTab />}
            {activeView === 'teamMembers' && <TeamMembersTab teamMembers={teamMembers} onRefresh={fetchData} />}
            {activeView === 'assets' && <AssetsTab assets={assets} teamMembers={teamMembers} onRefresh={fetchData} />}
            {activeView === 'services' && <ServicesTabImproved onNavigate={setActiveView} />}
            {activeView === 'mySchedule' && <ScheduleTab />}
            {activeView === 'flows' && <MiniFlowsTab onNavigate={setActiveView} />}
            {activeView === 'profitLoss' && <ProfitLossTab onNavigate={setActiveView} />}
            {activeView === 'revenue' && <RevenueTab onNavigate={setActiveView} />}
            {activeView === 'expenses' && <ExpensesTab onNavigate={setActiveView} />}
            {activeView === 'discounts' && <DiscountsTab />}
            {activeView === 'account' && <ProfileTab />}
            {activeView === 'payments' && <PaymentsTabSimple onNavigate={setActiveView} />}
            {activeView === 'partners' && <PartnersTab organizationId={organizationId} />}
            {activeView === 'shared-services' && <SharedServicesTab />}
            {activeView === 'branding' && <BrandingTab />}
            {activeView === 'waivers' && <WaiversTabClean />}
            {activeView === 'notifications' && <NotificationsTab />}
            {activeView === 'reviews' && <ReviewsTab />}
            {activeView === 'relations' && <RelationsTab initialFilter={relationsFilter} key={relationsFilter} onNavigate={setActiveView} />}
            {activeView === 'checkin' && <CheckInTab />}
            {activeView === 'systemHealth' && <SystemHealthTabSimple />}
            </div>
          </div>
        </div>
      </main>

      <SettingsDialog 
        open={settingsOpen} 
        onOpenChange={setSettingsOpen}
      />

      <ApprovalReviewDialog
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        requests={pendingRequests}
        team_members={teamMembers}
        onApprovalComplete={handleApprovalComplete}
      />

      <HelpPage
        isOpen={helpOpen}
        onClose={() => {
          setHelpOpen(false);
          setHelpSection(null);
        }}
        initialSection={helpSection}
        onStartTour={() => setShowOnboarding(true)}
      />

      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          localStorage.setItem('hasSeenOnboarding', 'true');
          setShowOnboarding(false);
          toast.success("Welcome to BookingFlows! You're all set!");
        }}
      />

      {/* Setup Guide Widget - Floating in bottom right - Only show for admins */}
      {isAdmin && (
        <>
          <SetupGuideWidget
            onNavigate={setActiveView}
            isVisible={showSetupGuide}
            onToggleVisibility={(visible) => {
              setShowSetupGuide(visible);
              if (!visible) {
                const key = getGuideStorageKey();
                localStorage.setItem(key, 'true');
                setShowGuideHighlight(true);
              }
            }}
            onOpenSettings={() => setSettingsOpen(true)}
          />
          
        </>
      )}
      </div>
    </div>
      )}
    </>
  );
};

export default DashboardV2;
